/**
 * Refresh Context
 * Gerencia refresh de conteúdo das páginas
 */

import React, { createContext, useContext, useRef, useState, useCallback, ReactNode } from 'react';
import { logger } from '@/utils/logger';

interface RefreshContextType {
  refresh: () => void | Promise<void>;
  setRefreshCallback: (callback: (() => void | Promise<void>) | null) => void;
  isRefreshing: boolean;
}

const RefreshContext = createContext<RefreshContextType | undefined>(undefined);

export const RefreshProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const refreshCallbackRef = useRef<(() => void | Promise<void>) | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    if (refreshCallbackRef.current) {
      setIsRefreshing(true);
      try {
        await refreshCallbackRef.current();
      } catch (error) {
        logger.error('Error refreshing:', error);
      } finally {
        setIsRefreshing(false);
      }
    }
  }, []);

  const setRefreshCallback = useCallback((callback: (() => void | Promise<void>) | null) => {
    refreshCallbackRef.current = callback;
  }, []);

  return (
    <RefreshContext.Provider value={{ refresh, setRefreshCallback, isRefreshing }}>
      {children}
    </RefreshContext.Provider>
  );
};

export const useRefresh = () => {
  const context = useContext(RefreshContext);
  if (!context) {
    throw new Error('useRefresh must be used within RefreshProvider');
  }
  return context;
};
